/**
 * Price Feed Service — polls energy oracle and weather forecast APIs,
 * updates perpStore.marketData with live prices.
 */

import { usePerpStore } from '../store/perpStore';
import type { MarketData } from '../store/perpStore';

const ENERGY_MAP: Record<string, string> = {
  'NATGAS-HH': 'natgas-hh',
  'NATGAS-TTF': 'natgas-ttf',
  'CRUDE-WTI': 'crude-wti',
  'CRUDE-BRENT': 'crude-brent',
  'GASOLINE': 'gasoline-rbob',
  'ULSD': 'ulsd',
};

const POLL_INTERVAL = 30_000;
let pollTimer: ReturnType<typeof setInterval> | null = null;
let lastPrices: Record<string, number> = {};

function defaultMarketData(): MarketData {
  return {
    markPrice: 0, indexPrice: 0, change24h: 0, change24hPct: 0,
    volume24h: 0, openInterest: 0, fundingRate: 0,
    nextFunding: Date.now() + 28800000, nextSettlement: Date.now() + 86400000,
    high24h: 0, low24h: 0,
  };
}

async function fetchEnergyPrices(): Promise<void> {
  try {
    const apiBase = window.location.hostname.includes('whether.market')
      ? 'https://api.whether.market' : '';
    const res = await fetch(`${apiBase}/api/energy/all`);
    if (!res.ok) return;
    const data = await res.json() as { products: Record<string, { price: number }> };
    const store = usePerpStore.getState();
    const currentData = { ...store.marketData };
    let updated = false;
    for (const [oracleId, marketId] of Object.entries(ENERGY_MAP)) {
      const product = data.products?.[oracleId];
      if (!product?.price) continue;
      const price = product.price;
      const prev = lastPrices[marketId] ?? price;
      const change = price - prev;
      const changePct = prev > 0 ? (change / prev) * 100 : 0;
      currentData[marketId] = {
        ...(currentData[marketId] ?? defaultMarketData()),
        markPrice: price, indexPrice: price,
        change24h: change, change24hPct: changePct,
        high24h: Math.max(currentData[marketId]?.high24h ?? 0, price),
        low24h: currentData[marketId]?.low24h ? Math.min(currentData[marketId].low24h, price) : price,
      };
      lastPrices[marketId] = price;
      updated = true;
    }
    if (updated) usePerpStore.setState({ marketData: currentData });
  } catch (e) { console.warn('Energy price fetch failed:', e); }
}

async function fetchWeatherPrices(): Promise<void> {
  try {
    const apiBase = window.location.hostname.includes('whether.market')
      ? 'https://api.whether.market' : '';
    for (const station of ['KJFK', 'KORD', 'KLAX', 'KIAH']) {
      try {
        const res = await fetch(`${apiBase}/api/forecast?location=${station}`);
        if (!res.ok) continue;
        const data = await res.json();
        const temp = data?.current?.temperature ?? data?.temperature ?? data?.temp;
        if (temp == null) continue;
        const tempF = typeof temp === 'number' ? temp : parseFloat(temp);
        const code = station.slice(1).toLowerCase();
        const hdd = Math.max(0, 65 - tempF);
        const cdd = Math.max(0, tempF - 65);
        const store = usePerpStore.getState();
        const currentData = { ...store.marketData };
        if (hdd > 0) currentData[`hdd-${code}-7d`] = { ...(currentData[`hdd-${code}-7d`] ?? defaultMarketData()), markPrice: hdd, indexPrice: hdd };
        if (cdd > 0) currentData[`cdd-${code}-7d`] = { ...(currentData[`cdd-${code}-7d`] ?? defaultMarketData()), markPrice: cdd, indexPrice: cdd };
        currentData[`temp-${code}-3d`] = { ...(currentData[`temp-${code}-3d`] ?? defaultMarketData()), markPrice: tempF, indexPrice: tempF };
        usePerpStore.setState({ marketData: currentData });
      } catch { /* station fetch failed */ }
    }
  } catch (e) { console.warn('Weather price fetch failed:', e); }
}

export function startPriceFeed(): void {
  if (pollTimer) return;
  fetchEnergyPrices();
  fetchWeatherPrices();
  pollTimer = setInterval(() => {
    fetchEnergyPrices();
    if (Math.random() < 0.33) fetchWeatherPrices();
  }, POLL_INTERVAL);
}

export function stopPriceFeed(): void {
  if (pollTimer) { clearInterval(pollTimer); pollTimer = null; }
}
